/* Small building blocks for the system shown in the proposal – the portal's own classes and tokens, rendered
   statically (no router, no data hooks). Everything here is inert: it is shown, not used. */
import { Bell, CalendarDays, ChartColumn, ChevronDown, ChevronsUpDown, Download, Gauge, Menu, Search, Settings, SlidersHorizontal, Waves, Wrench } from "lucide-react";
import { LensMark, ProximaLogo } from "@/components/brand/ProximaLogo";
import { UserAvatar } from "@/components/common/UserAvatar";
import { STATUS } from "@/lib/status";
import { cn } from "@/lib/utils";
import { CUSTOMER, USER } from "@/data/tilbod/hhDemo";

export const NAV = [
  { key: "oversikt", label: "Oversikt", icon: Gauge },
  { key: "kraftverk", label: "Kraftverk", icon: Waves },
  { key: "hendelser", label: "Hendelser", icon: Bell },
  { key: "rapporter", label: "Rapporter", icon: ChartColumn },
  { key: "service", label: "Service", icon: Wrench },
];
const AskIcon = (props) => <LensMark tone="mono" {...props} />;

/* ---------- type ---------- */
export const PageTitle = ({ children, className }) => <div className={cn("text-[28px] font-semibold leading-[1.15] tracking-[-0.015em] text-foreground", className)}>{children}</div>;
export const Meta = ({ children, className }) => <div className={cn("mt-1 text-[15px] text-muted-foreground", className)}>{children}</div>;
export const LabelCaps = ({ children, className }) => <div className={cn("label-caps", className)}>{children}</div>;
export const N = ({ children, className }) => <span className={cn("metric", className)}>{children}</span>;
export const TextLink = ({ children, className }) => <span className={cn("text-[13px] font-medium text-primary", className)}>{children}</span>;

/* one supporting figure: label, value, one line of context */
export const Figure = ({ label, value, unit, sub, size = "md" }) => (
  <div className="min-w-0">
    <div className="text-[13.5px] text-muted-foreground">{label}</div>
    <div className="mt-1 flex items-baseline gap-1.5">
      <span className={cn("metric font-semibold leading-none tracking-[-0.02em] text-foreground", size === "md" ? "text-[22px]" : "text-[17px]")}>{value}</span>
      {unit ? <span className="text-[13.5px] font-medium text-muted-foreground">{unit}</span> : null}
    </div>
    {sub ? <div className="mt-1.5 text-[13px] leading-snug text-muted-foreground">{sub}</div> : null}
  </div>
);

export const DeltaText = ({ value, suffix = "" }) => {
  const up = value > 0;
  return (
    <span className="metric inline-flex items-center gap-0.5 text-[13px] text-foreground">
      <span aria-hidden="true">{up ? "↑" : "↓"}</span>
      {up ? "+" : "−"}
      {Math.abs(value).toLocaleString("nb-NO", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %{suffix}
    </span>
  );
};

/* ---------- controls (static) ---------- */
export const Segmented = ({ items, value, className }) => (
  <div className={cn("segmented inline-flex items-center", className)}>
    {items.map((it) => (
      <span key={it} role="tab" data-state={it === value ? "active" : "inactive"} className="inline-flex h-8 items-center justify-center whitespace-nowrap rounded-[5px] px-3 text-[13.5px] font-medium">
        {it}
      </span>
    ))}
  </div>
);

export const RangePicker = ({ value = "7 d", custom = true }) => (
  <div className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md bg-muted p-0.5">
    {["24 t", "7 d", "30 d", "90 d", "1 år"].map((o) => (
      <span key={o} className={cn("inline-flex h-7 items-center rounded-[5px] px-2.5 text-xs font-medium", o === value ? "bg-card text-foreground shadow-[0_1px_2px_hsl(var(--brand-ink)/0.08)]" : "text-muted-foreground")}>
        {o}
      </span>
    ))}
    {custom ? (
      <span className="inline-flex h-7 items-center gap-1.5 rounded-[5px] px-2.5 text-xs font-medium text-muted-foreground">
        <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" /> Egendefinert
      </span>
    ) : null}
  </div>
);

export const Chip = ({ children, count, on = false }) => (
  <span data-state={on ? "on" : "off"} className="chip">
    {children}
    {count !== undefined ? <span className="metric text-muted-foreground/70">{count}</span> : null}
  </span>
);

export const SearchBox = ({ placeholder, className }) => (
  <div className={cn("relative flex h-9 items-center rounded-md border border-input bg-background pl-8 pr-3 text-[14px] text-muted-foreground", className)}>
    <Search className="pointer-events-none absolute left-2.5 h-4 w-4" aria-hidden="true" />
    <span className="truncate">{placeholder}</span>
  </div>
);

export const SelectStub = ({ children, className }) => (
  <span className={cn("inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground", className)}>
    {children}
    <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
  </span>
);

export const Btn = ({ children, variant = "outline", icon: Icon, className }) => (
  <span
    className={cn(
      "inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 text-[13.5px] font-medium",
      variant === "primary" && "bg-primary text-primary-foreground shadow-[var(--shadow-1)]",
      variant === "outline" && "border border-input bg-background text-foreground",
      variant === "ghost" && "text-muted-foreground",
      className,
    )}
  >
    {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
    {children}
  </span>
);

export const IconBtn = ({ icon: Icon, className }) => (
  <span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground", className)}>
    <Icon className="h-4 w-4" aria-hidden="true" />
  </span>
);

export const MoreMeasurements = () => <Btn variant="ghost" icon={SlidersHorizontal} className="px-2.5">Flere målinger</Btn>;
export const ExportBtn = () => <IconBtn icon={Download} />;

export const LineTabs = ({ items, value }) => (
  <div className="flex w-full items-end gap-5 border-b border-border">
    {items.map(([label, n]) => (
      <span key={label} className={cn("-mb-px inline-flex items-center border-b-2 pb-2.5 pt-1 text-[14px] font-medium", label === value ? "border-primary text-primary" : "border-transparent text-muted-foreground")}>
        {label}
        {n !== undefined ? <span className="metric ml-1.5 text-[12.5px] text-muted-foreground">{n}</span> : null}
      </span>
    ))}
  </div>
);

/* ---------- status ---------- */
export const StatusWord = ({ status, reason, size = "md" }) => {
  const cfg = STATUS[status] || STATUS.running;
  if (status === "running") return <span className={cn("text-muted-foreground", size === "md" ? "text-[14.5px]" : "text-[13.5px]")}>{cfg.label}</span>;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className={cn("status-dot", `status-dot-${status}`)} aria-hidden="true" />
      <span className={cn("truncate font-medium", cfg.text, size === "md" ? "text-[14.5px]" : "text-[13.5px]")}>
        {cfg.label}
        {reason ? <span className="font-normal text-foreground"> · {reason}</span> : null}
      </span>
    </span>
  );
};

const PILL = {
  open: "border-rose-500/30 text-rose-600",
  acknowledged: "border-amber-500/30 text-amber-700",
  resolved: "border-border text-muted-foreground",
  mottatt: "border-sky-500/30 text-sky-700",
  planlagt: "border-primary/30 text-primary",
  pagar: "border-amber-500/30 text-amber-700",
  fullfort: "border-emerald-500/30 text-emerald-700",
};
const PILL_DOT = { open: "bg-rose-500", acknowledged: "bg-amber-500", resolved: "bg-muted-foreground/50", mottatt: "bg-sky-500", planlagt: "bg-primary", pagar: "bg-amber-500", fullfort: "bg-emerald-500" };
const PILL_LABEL = { open: "Åpen", acknowledged: "Kvittert", resolved: "Lukket", mottatt: "Mottatt", planlagt: "Planlagt", pagar: "Pågår", fullfort: "Fullført" };
export const Pill = ({ status }) => (
  <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11.5px] font-medium", PILL[status])}>
    <span className={cn("h-1.5 w-1.5 rounded-full", PILL_DOT[status])} aria-hidden="true" />
    {PILL_LABEL[status]}
  </span>
);

const SEV = { critical: ["Kritisk", "text-rose-600", "bg-rose-500"], warning: ["Advarsel", "text-amber-700", "bg-amber-500"], info: ["Info", "text-muted-foreground", "bg-slate-400"] };
export const Severity = ({ severity }) => {
  const [label, text, dot] = SEV[severity] || SEV.info;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[13px] font-medium", text)}>
      <span className={cn("status-dot", dot)} aria-hidden="true" />
      {label}
    </span>
  );
};

/* ---------- shell pieces ---------- */
export const CustomerRow = ({ className }) => (
  <div className={cn("flex h-11 items-center gap-3 px-2.5", className)}>
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] bg-primary text-[13px] font-semibold text-primary-foreground" aria-hidden="true">
      V
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[14.5px] font-semibold leading-[1.2] text-foreground">{CUSTOMER.short_name}</span>
      <span className="metric block truncate text-[12px] leading-[1.2] text-muted-foreground">26 kraftverk</span>
    </span>
  </div>
);

export const RailLink = ({ item, active, trailing }) => {
  const Icon = item.icon;
  return (
    <span className={cn("rail-link", active && "active")}>
      <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} aria-hidden="true" />
      <span className="flex-1 truncate">{item.label}</span>
      {trailing}
    </span>
  );
};

export const Sidebar = ({ active, eventsCount = 3 }) => (
  <div className="rail flex h-full w-[224px] shrink-0 flex-col border-r border-rail-border">
    <div className="px-5 pt-5">
      <ProximaLogo tagline="HydroHub" className="text-[15px]" />
    </div>
    <div className="mt-5 px-3">
      <CustomerRow />
    </div>
    <div className="mt-3 px-3">
      <span className="rail-search">
        <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate">Søk</span>
        <span className="kbd">⌘K</span>
      </span>
    </div>
    <div className="mt-5 flex-1 px-3">
      <div className="space-y-0.5">
        {NAV.map((item) => (
          <RailLink key={item.key} item={item} active={active === item.key} trailing={item.key === "hendelser" && eventsCount ? <span className="metric text-[13px] font-semibold tabular-nums text-rose-700">{eventsCount}</span> : null} />
        ))}
      </div>
      <div className="mt-6 space-y-0.5">
        <RailLink item={{ key: "ai", label: "Spør HydroHub", icon: AskIcon }} active={active === "ai"} />
      </div>
    </div>
    <div className="space-y-0.5 px-3 pb-2">
      <RailLink item={{ key: "innstillinger", label: "Innstillinger", icon: Settings }} />
    </div>
    <div className="px-3 pb-3 pt-1">
      <span className="flex h-12 w-full items-center gap-2.5 rounded-md px-2 text-left">
        <UserAvatar user={USER} size="md" testId="hh-avatar" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-medium leading-[1.25]">{USER.name}</span>
          <span className="block truncate text-[12.5px] leading-[1.25] text-muted-foreground">{USER.title}</span>
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </span>
    </div>
  </div>
);

/* compact (no sidebar) and phone: a slim top bar */
export const TopBar = ({ mode }) => (
  <div className="flex h-[52px] shrink-0 items-center justify-between gap-3 border-b border-border bg-background px-4">
    <div className="flex items-center gap-4">
      <ProximaLogo tagline="HydroHub" className="text-[14px]" />
      {mode === "compact" ? <CustomerRow className="h-9 border-l border-border pl-4" /> : null}
    </div>
    <div className="flex items-center gap-1">
      {mode === "compact" ? (
        <span className="rail-search mr-2 w-[220px]">
          <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 truncate">Søk</span>
          <span className="kbd">⌘K</span>
        </span>
      ) : (
        <IconBtn icon={Search} />
      )}
      <UserAvatar user={USER} size="md" testId="hh-avatar-top" />
    </div>
  </div>
);

export const BottomNav = ({ active }) => (
  <div className="absolute inset-x-0 bottom-0 grid h-[64px] grid-cols-5 border-t border-border bg-background/95 pb-1">
    {[...NAV.slice(0, 3), { key: "ai", label: "Spør", icon: AskIcon }, { key: "mer", label: "Mer", icon: Menu }].map((it) => {
      const Icon = it.icon;
      const on = active === it.key || (it.key === "mer" && ["rapporter", "service"].includes(active));
      return (
        <span key={it.key} className={cn("flex flex-col items-center justify-center gap-1 text-[11px] font-medium", on ? "text-primary" : "text-muted-foreground")}>
          <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          {it.label}
        </span>
      );
    })}
  </div>
);
